import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import App from './pages';
import { Provider } from 'react-redux';
import { createBrowserHistory } from 'history';
import { useTranslation } from 'react-i18next';
import { Router } from 'react-router';
import { ConfigProvider } from 'antd';
import enGB from 'antd/es/locale/en_GB';
import deDE from 'antd/es/locale/de_DE';
import esES from 'antd/es/locale/es_ES';
import csCZ from 'antd/es/locale/cs_CZ';
import jaJP from 'antd/es/locale/ja_JP';
import skSK from 'antd/es/locale/sk_SK';
import itIT from 'antd/es/locale/it_IT';
import frFR from 'antd/es/locale/fr_FR';
import plPL from 'antd/es/locale/pl_PL';
import ptPT from 'antd/es/locale/pt_PT';
import ptBR from 'antd/es/locale/pt_BR';
import zhTW from 'antd/es/locale/zh_TW';
import moment from 'moment';
import { config as fontAwesomeConfig } from '@fortawesome/fontawesome-svg-core';
import '@assets/fonts';
import 'moment/locale/en-gb';
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/cs';
import 'moment/locale/ja';
import 'moment/locale/sk';
import 'moment/locale/it';
import 'moment/locale/fr';
import 'moment/locale/pl';
import 'moment/locale/pt';
import 'moment/locale/pt-br';
import 'moment/locale/zh-tw';
import TokenPool from './redux/TokenPool';
import OrganisationDetailsCache from '@utils/organisationDetailsCache';
import { initialise as initialiseAnalytics, trackApplicationEvent } from './analytics';
import createStore from './store';
import * as serviceWorker from './serviceWorker';
import { resolveLanguageCode } from './utils/supportedLanguages';
import './i18n';
import './style.less';

// FontAwesome SVG Core by default adds its own CSS style tags at runtime
// which breaks with a strict CSP.
// As per instructions at https://docs.fontawesome.com/web/dig-deeper/security/ :
// To fix this the node_modules/fontawesome-svg-core/styles.css file needs to be copied
// and manually imported to be included in the build.
// In addition we need to set the fontAwesomeConfig.autoAddCss property to false.
import './theme/fontawesome-svg-styles.css';
// Make sure this is before any other `fontawesome` API calls
fontAwesomeConfig.autoAddCss = false;

const history = createBrowserHistory();

const tokens = new TokenPool();
const organisationDetailsCache = new OrganisationDetailsCache(tokens);

const store = createStore({ history, tokens, organisationDetailsCache });

initialiseAnalytics();
trackApplicationEvent('Loading');

const antdLocales = {
  en: enGB,
  de: deDE,
  es: esES,
  cs: csCZ,
  ja: jaJP,
  sk: skSK,
  it: itIT,
  fr: frFR,
  pl: plPL,
  pt: ptPT,
  'pt-br': ptBR,
  'zh-hk': zhTW,
};

const momentLocales = {
  en: 'en-gb',
  de: 'de',
  es: 'es',
  cs: 'cs',
  ja: 'ja',
  sk: 'sk',
  it: 'it',
  fr: 'fr',
  pl: 'pl',
  pt: 'pt',
  'pt-br': 'pt-br',
  'zh-hk': 'zh-tw',
};

const getLocale = lang => antdLocales[resolveLanguageCode(lang)] || enGB;

const Application = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const locale = useMemo(() => getLocale(language), [language]);
  // const locale = enGB;

  moment.locale(momentLocales[resolveLanguageCode(language)] || 'en-gb');

  return (
    <Provider store={store}>
      <ConfigProvider locale={locale} wave={{ disabled: true }}>
        <Router history={history}>
          <App />
        </Router>
      </ConfigProvider>
    </Provider>
  );
};

ReactDOM.render(<Application />, document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
